import { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Card, Text, Title } from '@bancobarigui/barihaus';
import { Input as AntInput, message } from 'antd';
import { PlusOutlined, CloseOutlined, DeleteOutlined, FileOutlined, UploadOutlined } from '@ant-design/icons';
import { inspectionService } from '@/services/inspection.service';
import { docmanService } from '@/services/docman.service';

interface AddAttachmentModalProps {
  open: boolean;
  itemId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddAttachmentModal({ open, itemId, onClose, onSuccess }: AddAttachmentModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSelectedFile(null);
    }
  }, [open, form]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024;

      if (file.size > maxSize) {
        message.error('O arquivo deve ter no máximo 10MB');
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
      form.setFieldValue('file', file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    form.setFieldValue('file', null);
  };

  const handleSubmit = async (values: any) => {
    if (!selectedFile) {
      message.error('Selecione um arquivo para anexar');
      return;
    }

    setLoading(true);
    try {
      message.loading({ content: 'Enviando arquivo para S3...', key: 'upload' });

      const uploadResult = await docmanService.uploadFile(
        itemId,
        selectedFile,
        'OUTROS',
      );

      if (!uploadResult.documentId) {
        throw new Error('Docman não retornou documentId');
      }

      message.success({ content: 'Arquivo enviado com sucesso!', key: 'upload' });

      const payload = {
        description: values.description,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type || 'application/octet-stream',
        filePath: `docman/${uploadResult.documentId}`,
      };

      await inspectionService.addAttachmentToItem(itemId, payload);

      message.success('Anexo adicionado com sucesso!');

      form.resetFields();
      setSelectedFile(null);
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao adicionar anexo';
      message.error({ content: errorMessage, key: 'upload', duration: 5 });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedFile(null);
    onClose();
  };

  return (
    <Modal
      centered
      open={open}
      width={700}
      footer={null}
      onCancel={handleCancel}
      className="space-y-8"
      closeIcon={<CloseOutlined />}
    >
      <Title level={2}>Adicionar Anexo</Title>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className="space-y-4">
          <Text>Arquivo</Text>

          {!selectedFile ? (
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary transition-colors">
              <input
                type="file"
                id="file-upload-attachment"
                className="!hidden"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
              />
              <label
                htmlFor="file-upload-attachment"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <UploadOutlined className="text-4xl text-gray-400" />
                <div>
                  <Text className="block text-primary font-medium">
                    Clique para selecionar um arquivo
                  </Text>
                  <Text className="block text-xs text-gray-500 mt-1">
                    PDF, DOC, XLS, JPG, PNG, ZIP (máx. 10MB)
                  </Text>
                </div>
              </label>
            </div>
          ) : (
            <Card className="!p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileOutlined className="text-2xl text-primary" />
                  <div>
                    <Text className="block font-medium">{selectedFile.name}</Text>
                    <Text className="block text-xs text-gray-500">
                      {formatFileSize(selectedFile.size)} • {selectedFile.type || 'Arquivo'}
                    </Text>
                  </div>
                </div>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={handleRemoveFile}>
                  Remover
                </Button>
              </div>
            </Card>
          )}

          <Form.Item
            name="file"
            rules={[{ required: true, message: 'Selecione um arquivo' }]}
            style={{ display: 'none' }}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descrição"
            rules={[{ required: true, message: 'Informe uma descrição para o anexo.' }]}
          >
            <AntInput.TextArea
              rows={3}
              placeholder="Ex: Documento de identidade, Comprovante de endereço, etc."
            />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={handleCancel}>
            Cancelar
          </Button>
          <Button
            buttonColor="primary"
            htmlType="submit"
            loading={loading}
            icon={<PlusOutlined />}
          >
            Adicionar Anexo
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
